import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts';
import { SupportLayout } from './SupportLayout';

const SUPPORT_EMAIL = 'hello@kodefoundry.com';
const WHATSAPP = '+9613419348';

const ContactForm: React.FC = () => {
  const { user } = useAuth();
  const [name, setName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) {
      toast.error('Email and message are required');
      return;
    }
    setSending(true);
    try {
      await addDoc(collection(db, 'supportMessages'), {
        name: name.trim() || 'Anonymous',
        email: email.trim(),
        subject: subject.trim() || 'General enquiry',
        message: message.trim(),
        status: 'open',
        userId: user?.uid || null,
        createdAt: serverTimestamp(),
      });
      setSent(true);
      setSubject('');
      setMessage('');
      toast.success('Message sent. We\'ll get back to you.');
    } catch {
      toast.error('Could not send. Try email or WhatsApp instead.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="p-6 bg-primary-50 border border-primary-200 rounded-2xl not-prose">
        <p className="font-medium text-gray-900">Thanks, your message is in.</p>
        <p className="text-sm text-gray-600 mt-1">
          We reply by email, usually within a few hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 not-prose">
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          required
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="How can we help?"
        rows={4}
        required
        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
      <button
        type="submit"
        disabled={sending}
        className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50"
      >
        {sending ? 'Sending...' : 'Send message'}
      </button>
    </form>
  );
};

export const ContactPage: React.FC = () => {
  return (
    <SupportLayout
      title="Contact us"
      subtitle="A real human on the Beirut team picks up day or night."
    >
      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="block p-6 border border-gray-200 rounded-2xl hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="text-2xl mb-2">✉️</div>
          <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
          <p className="text-sm text-gray-600">{SUPPORT_EMAIL}</p>
          <p className="text-xs text-gray-500 mt-2">Reply within a few hours.</p>
        </a>

        <a
          href={`https://wa.me/${WHATSAPP.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-6 border border-gray-200 rounded-2xl hover:border-primary-300 hover:shadow-md transition-all"
        >
          <div className="text-2xl mb-2">💬</div>
          <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
          <p className="text-sm text-gray-600">{WHATSAPP}</p>
          <p className="text-xs text-gray-500 mt-2">Fastest way to reach us.</p>
        </a>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Send us a message</h2>
      <ContactForm />

      <h2 className="text-2xl font-semibold text-gray-900 mb-4 mt-10">Pick the right channel</h2>
      <ul className="space-y-3 text-gray-700">
        <li>
          <strong className="text-gray-900">Booking issue, refund, dispute:</strong> WhatsApp. Include your booking
          confirmation code.
        </li>
        <li>
          <strong className="text-gray-900">Listing your place, host onboarding:</strong> email or WhatsApp.
        </li>
        <li>
          <strong className="text-gray-900">Press, partnerships:</strong> email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary-600 underline">
            {SUPPORT_EMAIL}
          </a>
          .
        </li>
        <li>
          <strong className="text-gray-900">Emergency at the property:</strong> WhatsApp first. We'll route you to
          the host and step in if needed.
        </li>
      </ul>

      <div className="mt-12 p-6 bg-gray-50 rounded-2xl">
        <h3 className="font-semibold text-gray-900 mb-2">Office</h3>
        <p className="text-gray-600">Beirut, Lebanon</p>
        <p className="text-sm text-gray-500 mt-2">
          Walk-ins by appointment only. Email us first to set a time.
        </p>
      </div>
    </SupportLayout>
  );
};

export default ContactPage;
